import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { CharlieQrCode } from '@ver.id/libs.ui.charlie';
import { useTranslation } from '../../i18n/i18n';
import { installLinksMap } from '../../data/installLinks';
import { installPageUrl } from '../../utils/appLinks';
import appStoreBadge from '../../assets/app-store.svg';
import playStoreBadge from '../../assets/play-store.svg';

type Props = {
  /** The app's name, used to look up its store links. */
  appName?: string;
  /** How the app is named in the copy, e.g. with its article. */
  displayName: string;
};

/**
 * HostedInstallInstructions — How to install an identity app: a QR code that opens
 * this page on a phone plus store badges, or a notice when no install links
 * are known.
 *
 * Renders sibling blocks so the parent's column controls their spacing.
 */
export default function HostedInstallInstructions({
  appName,
  displayName,
}: Props) {
  const { t } = useTranslation();
  const links = appName ? installLinksMap[appName] : undefined;

  if (!appName || !links) {
    return (
      <div className="flex flex-col gap-2 items-center text-center">
        <FontAwesomeIcon
          icon={faCircleInfo}
          className="size-10 text-muted-foreground"
        />
        <h2 className="text-xl font-semibold text-foreground">
          {t('installInstructions.unavailableTitle')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('installInstructions.unavailableBody')}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 items-center text-center">
        <h2 className="text-xl font-semibold text-foreground">
          {t('installInstructions.title', { appName: displayName })}
        </h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          {t('installInstructions.subtitle', { appName: displayName })}
        </p>
      </div>

      <div className="flex flex-col gap-6 items-center">
        <CharlieQrCode data={installPageUrl(appName)} />
        <div className="flex items-center justify-center gap-3 h-8">
          {links.apple && (
            <a
              href={links.apple}
              target="_blank"
              rel="noopener noreferrer"
              className="h-full"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className="h-full w-auto"
              />
            </a>
          )}
          {links.android && (
            <a
              href={links.android}
              target="_blank"
              rel="noopener noreferrer"
              className="h-full"
            >
              <img
                src={playStoreBadge}
                alt="Get it on Google Play"
                className="h-full w-auto"
              />
            </a>
          )}
        </div>
      </div>
    </>
  );
}
